import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import "./ModifierAnimal.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { GiCow } from "react-icons/gi";
import { FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

export default function ModifierAnimal() {
  const { t } = useTranslation();
  const { id } = useParams(); // ✅ récupère l'ID depuis l'URL
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    matricule: "",
    sexe: "",
    date_naissance: "",
    race: "",
    categorie: "",
    robe: "",
    statut: "actif",
    valeur: 0,
    troupeau: "",
    pere: "",
    mere: "",
    ranch_provenance: "",
  });

  const [initialData, setInitialData] = useState(null);
  const [animaux, setAnimaux] = useState([]);
  const [troupeaux, setTroupeaux] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [races, setRaces] = useState([]);
  const [robes, setRobes] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewRaceInput, setShowNewRaceInput] = useState(false);
  const [showNewRobeInput, setShowNewRobeInput] = useState(false);
  const [showNewRanchInput, setShowNewRanchInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newRaceName, setNewRaceName] = useState("");
  const [newRobeName, setNewRobeName] = useState("");
  const [newRanchName, setNewRanchName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    sexe: "",
    categorie: "",
    date_naissance: "",
    troupeau: "",
    pere: "",
    mere: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    sexe: false,
    categorie: false,
    date_naissance: false,
    troupeau: false,
    pere: false,
    mere: false,
  });

     // ✅ Charger les infos de l'animal spécifique
  useEffect(() => {
    // Note: Utilisation d'un chemin d'API correct
    const fetchAnimal = async () => {
      try {
        const response = await api.get(`/api/animaux/${id}/`);
        const data = response.data;
        
        // Si l'animal est vendu ou mort, rediriger vers la liste avec un message
        if (data.statut === "vendu" || data.statut === "mort") {
          toast.error(t.cannotEditSoldOrDead);
          setTimeout(() => {
            navigate("/animaux");
          }, 1500);
          return;
        }
        
        const normalizedData = {
          ...data,
          categorie: data.categorie && typeof data.categorie === 'object' ? data.categorie.id || data.categorie : data.categorie,
          race: data.race && typeof data.race === 'object' ? data.race.id || data.race : data.race,
          robe: data.robe && typeof data.robe === 'object' ? data.robe.id || data.robe : data.robe,
          ranch_provenance: data.ranch_provenance && typeof data.ranch_provenance === 'object' ? data.ranch_provenance.id || data.ranch_provenance : data.ranch_provenance,
        };
        setFormData(normalizedData);
        setInitialData(JSON.parse(JSON.stringify(normalizedData))); // Deep copy pour comparaison
      } catch (error) {
        console.error("Erreur lors du chargement de l'animal:", error);
      }
    };
    fetchAnimal();
  }, [id, navigate]);

  // ✅ Charger les listes des animaux (pour père/mère), des troupeaux, des ranches et des catégories
  useEffect(() => {
    const fetchData = async () => {
      const results = await Promise.allSettled([
        api.get("/api/animaux/"),
        api.get("/api/troupeaux/"),
        api.get("/api/ranches/"),
        api.get("/api/categories/"),
        api.get("/api/races/"),
        api.get("/api/robes/"),
      ]);
      
      // Mettre à jour chaque état uniquement si la requête correspondante a réussi
      // L'ordre des résultats correspond à l'ordre des requêtes
      if (results[0].status === "fulfilled") {
        setAnimaux(results[0].value.data);
      } else {
        console.error("Erreur lors du chargement des animaux:", results[0].reason);
        setAnimaux([]); // Valeur par défaut sûre
      }
      
      if (results[1].status === "fulfilled") {
        setTroupeaux(results[1].value.data);
      } else {
        console.error("Erreur lors du chargement des troupeaux:", results[1].reason);
        setTroupeaux([]); // Valeur par défaut sûre
      }
      
      if (results[2].status === "fulfilled") {
        setRanches(results[2].value.data);
      } else {
        console.error("Erreur lors du chargement des ranches:", results[2].reason);
        setRanches([]); // Valeur par défaut sûre
      }
      
      if (results[3].status === "fulfilled") {
        setCategories(results[3].value.data);
      } else {
        console.error("Erreur lors du chargement des catégories:", results[3].reason);
        setCategories([]); // Valeur par défaut sûre
      }
      
      if (results[4].status === "fulfilled") {
        setRaces(results[4].value.data);
      } else {
        console.error("Erreur lors du chargement des races:", results[4].reason);
        setRaces([]); // Valeur par défaut sûre
      }
      
      if (results[5].status === "fulfilled") {
        setRobes(results[5].value.data);
      } else {
        console.error("Erreur lors du chargement des robes:", results[5].reason);
        setRobes([]); // Valeur par défaut sûre
      }
    };
    fetchData();
  }, []); // Exécute une seule fois au montage du composant

  // Fonction de validation des champs obligatoires
  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  // Validation d'un champ spécifique
  // Dans ModifierAnimal, on considère qu'un animal avec date_naissance est né
  const validateField = (name, value, dateNaissance = formData.date_naissance) => {
    // Calculer isNaissance à partir de la valeur de date_naissance fournie
    const isNaissance = dateNaissance && typeof dateNaissance === 'string' && dateNaissance.trim() !== "";
    
    // Champs toujours obligatoires
    if (name === "sexe" || name === "categorie" || name === "troupeau") {
      return validateRequiredField(name, value);
    }
    
    // Champs conditionnels pour les naissances
    if (isNaissance) {
      if (name === "date_naissance" || name === "pere" || name === "mere") {
        return validateRequiredField(name, value);
      }
    }
    
    return "";
  };

  // ✅ Gérer la saisie
  const handleChange = (e) => {
    const { name, value } = e.target;
 let newValue = value;

// Convertir les valeurs en nombres pour les clés étrangères
 if (name === "troupeau" || name === "pere" || name === "mere" || name === "ranch_provenance") {
   newValue = value ? Number(value) : null;
 } else if (name === "valeur") {
   newValue = parseFloat(value) || 0;
 }
    setFormData({ ...formData, [name]: newValue });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      // Si c'est date_naissance qui change, utiliser la nouvelle valeur, sinon utiliser formData.date_naissance
      const currentDateNaissance = name === "date_naissance" ? newValue : formData.date_naissance;
      const error = validateField(name, newValue, currentDateNaissance);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let fieldValue = value;
    
    // Convertir la valeur si nécessaire
    if (name === "troupeau" || name === "pere" || name === "mere" || name === "ranch_provenance") {
      fieldValue = value ? Number(value) : null;
    }
    
    // Marquer le champ comme touché
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }
    
    // Valider le champ (utiliser formData.date_naissance actuel)
    const error = validateField(name, fieldValue, formData.date_naissance);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que l'animal n'est pas vendu ou mort
    if (formData.statut === "vendu" || formData.statut === "mort") {
      toast.error(t.cannotEditSoldOrDead);
      navigate("/animaux");
      return;
    }
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      sexe: true,
      categorie: true,
      date_naissance: true,
      troupeau: true,
      pere: true,
      mere: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires (passer formData.date_naissance actuel à chaque appel)
    const newFieldErrors = {
      sexe: validateField("sexe", formData.sexe, formData.date_naissance),
      categorie: validateField("categorie", formData.categorie || newCategoryName, formData.date_naissance),
      date_naissance: validateField("date_naissance", formData.date_naissance, formData.date_naissance),
      troupeau: validateField("troupeau", formData.troupeau, formData.date_naissance),
      pere: validateField("pere", formData.pere, formData.date_naissance),
      mere: validateField("mere", formData.mere, formData.date_naissance),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    try {
      const dataToSend = { ...formData };
      
      // Si une nouvelle catégorie est saisie, utiliser categorie_nom_input
      if (showNewCategoryInput && newCategoryName) {
        dataToSend.categorie_nom_input = newCategoryName;
        delete dataToSend.categorie; // Ne pas envoyer categorie si on crée une nouvelle
      } else if (dataToSend.categorie) {
        // Sinon, utiliser l'ID de la catégorie sélectionnée
        dataToSend.categorie = Number(dataToSend.categorie);
        delete dataToSend.categorie_nom_input;
      } else {
        // Pas de catégorie spécifiée
        delete dataToSend.categorie;
        delete dataToSend.categorie_nom_input;
      }

      // Race : ID prioritaire sinon création
      if (showNewRaceInput && newRaceName) {
        dataToSend.race_nom_input = newRaceName;
        delete dataToSend.race;
      } else if (dataToSend.race) {
        dataToSend.race = Number(dataToSend.race);
        delete dataToSend.race_nom_input;
      } else {
        delete dataToSend.race;
        delete dataToSend.race_nom_input;
      }

      // Robe : ID prioritaire sinon création
      if (showNewRobeInput && newRobeName) {
        dataToSend.robe_nom_input = newRobeName;
        delete dataToSend.robe;
      } else if (dataToSend.robe) {
        dataToSend.robe = Number(dataToSend.robe);
        delete dataToSend.robe_nom_input;
      } else {
        delete dataToSend.robe;
        delete dataToSend.robe_nom_input;
      }

      // Ranch de provenance : ID prioritaire sinon création
      if (showNewRanchInput && newRanchName) {
        dataToSend.ranch_provenance_nom_input = newRanchName;
        delete dataToSend.ranch_provenance;
      } else if (dataToSend.ranch_provenance) {
        dataToSend.ranch_provenance = Number(dataToSend.ranch_provenance);
        delete dataToSend.ranch_provenance_nom_input;
      } else {
        delete dataToSend.ranch_provenance;
        delete dataToSend.ranch_provenance_nom_input;
      }
      
      await api.put(`/api/animaux/${id}/`, dataToSend);
      toast.success(t.animalModifiedSuccess);
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/animaux");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Erreur lors de la mise à jour de l'animal.";
      toast.error(errorMessage);
    }
  };

  // Vérifier si des modifications ont été apportées
  const hasChanges = () => {
    if (!initialData) return false;
    
    // Comparer tous les champs (matricule exclu car il ne peut pas être modifié)
    const fieldsToCompare = [
      'sexe', 'date_naissance', 'race', 'race_nom_input', 'categorie', 'categorie_nom_input',
      'robe', 'robe_nom_input', 'statut', 'valeur', 'troupeau', 'pere', 'mere', 'ranch_provenance', 'ranch_provenance_nom_input'
    ];
    
    return fieldsToCompare.some(field => {
      const current = formData[field];
      const initial = initialData[field];
      
      // Gérer les cas spéciaux (nombres, null, etc.)
      if (field === 'valeur') {
        return parseFloat(current) !== parseFloat(initial || 0);
      }
      if (field === 'troupeau' || field === 'pere' || field === 'mere' || field === 'ranch_provenance') {
        return (current || null) !== (initial || null);
      }
      
      return String(current || '') !== String(initial || '');
    });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/animaux/${id}/`);
      setShowDeleteModal(false);
      toast.success(t.animalDeletedSuccess);
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
        navigate("/animaux");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Erreur lors de la suppression de l'animal.";
      toast.error(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  

  return (
    <div className="modifier-animal-container animaux-page">
      <div className="title-badge">
        <GiCow className="page-title-icon" aria-hidden="true" />
        <span>{t.modifyAnimal} {formData.matricule}</span>
      </div>
      <form onSubmit={handleSubmit} className="animal-form">
         {/* Groupe 1: Informations générales */}
          <div className="form-section-container">
            <div className="form">
              <label>{t.matricule}</label>
        <input
          type="text"
          name="matricule"
          value={formData.matricule}
          onChange={handleChange}
          placeholder={t.matricule}
          disabled
          className="disabled-input"
        />
        </div>
        <div className="form">
            <label>{t.birthDate}</label>
          <input
            type="date"
            name="date_naissance"
            value={formData.date_naissance}
            onChange={handleChange}
          />
          </div>
        <div className="form">
            <label>{t.breed}</label>
        <select
          name="race"
          value={formData.race || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "new") {
              setShowNewRaceInput(true);
              setNewRaceName("");
              setFormData({ ...formData, race: "", race_nom_input: "" });
            } else {
              setShowNewRaceInput(false);
              setNewRaceName("");
              setFormData({ ...formData, race: value ? Number(value) : "", race_nom_input: "" });
            }
          }}
        >
          <option value="">{t.selectBreed}</option>
          {races.map((race) => (
            <option key={race.id} value={race.id}>
              {race.nom}
            </option>
          ))}
          <option value="new">{t.createNewBreed}</option>
        </select>
        {showNewRaceInput && (
          <input
            type="text"
            name="race_nom_input"
            placeholder={t.newBreedName}
            value={newRaceName}
            onChange={(e) => {
              setNewRaceName(e.target.value);
              setFormData({ ...formData, race_nom_input: e.target.value, race: "" });
            }}
            style={{ marginTop: "8px" }}
          />
        )}
        </div>
        <div className="form">
            <label>{t.coat}</label>
        <select
          name="robe"
          value={formData.robe || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "new") {
              setShowNewRobeInput(true);
              setNewRobeName("");
              setFormData({ ...formData, robe: "", robe_nom_input: "" });
            } else {
              setShowNewRobeInput(false);
              setNewRobeName("");
              setFormData({ ...formData, robe: value ? Number(value) : "", robe_nom_input: "" });
            }
          }}
        >
          <option value="">{t.selectCoat}</option>
          {robes.map((robe) => (
            <option key={robe.id} value={robe.id}>
              {robe.nom}
            </option>
          ))}
          <option value="new">{t.createNewCoat}</option>
        </select>
        {showNewRobeInput && (
          <input
            type="text"
            name="robe_nom_input"
            placeholder={t.newCoatName}
            value={newRobeName}
            onChange={(e) => {
              setNewRobeName(e.target.value);
              setFormData({ ...formData, robe_nom_input: e.target.value, robe: "" });
            }}
            style={{ marginTop: "8px" }}
          />
        )}
        </div>
        <div className="form">
            <label>
              {t.category} <span className="required-indicator">*</span>
            </label>
            <select
              name="categorie"
              value={formData.categorie || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewCategoryInput(true);
                  setFormData({ ...formData, categorie: "", categorie_nom_input: "" });
                } else {
                  setShowNewCategoryInput(false);
                  setNewCategoryName("");
                  setFormData({ ...formData, categorie: value ? Number(value) : "", categorie_nom_input: "" });
                }
                // Valider après changement
                if (touchedFields.categorie || fieldErrors.categorie) {
                  const error = validateField("categorie", value === "new" ? newCategoryName : (value ? Number(value) : ""), formData.date_naissance);
                  setFieldErrors(prev => ({
                    ...prev,
                    categorie: error
                  }));
                }
              }}
              onBlur={handleBlur}
              className={fieldErrors.categorie ? "input-error" : ""}
            >
              <option value="">{t.selectCategory}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom} {cat.sexe && `(${cat.sexe === "M" ? t.male : t.female})`}
                </option>
              ))}
              <option value="new">{t.createNewCategory}</option>
            </select>
            
            {showNewCategoryInput && (
              <input
                type="text"
                name="categorie_nom_input"
                placeholder={t.newCategoryName}
                value={newCategoryName}
                onChange={(e) => {
                  setNewCategoryName(e.target.value);
                  setFormData({ ...formData, categorie_nom_input: e.target.value, categorie: "" });
                  // Valider la nouvelle catégorie
                  if (touchedFields.categorie || fieldErrors.categorie) {
                    const error = validateField("categorie", e.target.value, formData.date_naissance);
                    setFieldErrors(prev => ({
                      ...prev,
                      categorie: error
                    }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, categorie: true }));
                  const error = validateField("categorie", newCategoryName, formData.date_naissance);
                  setFieldErrors(prev => ({ ...prev, categorie: error }));
                }}
                className={fieldErrors.categorie ? "input-error" : ""}
                style={{ marginTop: "8px" }}
              />
            )}
            {fieldErrors.categorie && (
              <span className="field-error-message">{fieldErrors.categorie}</span>
            )}
        </div>
        <div className="form">
            <label>
              {t.sex} <span className="required-indicator">*</span>
            </label>
        <select
          name="sexe"
          value={formData.sexe}
          onChange={handleChange}
          onBlur={handleBlur}
          className={fieldErrors.sexe ? "input-error" : ""}
        >
          <option value="">{t.chooseSex}</option>
          <option value="M">{t.male}</option>
          <option value="F">{t.female}</option>
        </select>
        {fieldErrors.sexe && (
          <span className="field-error-message">{fieldErrors.sexe}</span>
        )}
        </div>
        <div className="form">
            <label>{t.statusLabel}</label>
        <select
          name="statut"
          value={formData.statut}
          onChange={handleChange}
        >
          <option value="actif">Actif</option>
          <option value="vendu">Vendu</option>
          <option value="mort">Mort</option>
        </select>
        </div>
        <div className="form">
            <label>{t.value}</label>
        <input
          type="number"
          name="valeur"
          value={formData.valeur}
          onChange={handleChange}
          placeholder={t.value}
        />
        </div>
         <div className="form">
            <label>
              Troupeau <span className="required-indicator">*</span>
            </label>
        <select
            name="troupeau"
            value={formData.troupeau}
            onChange={handleChange}
            onBlur={handleBlur}
            className={fieldErrors.troupeau ? "input-error" : ""}
            required
          >
            <option value="">-- Sélectionner un troupeau --</option>
            {troupeaux.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nom}
              </option>
            ))}
          </select>
          {fieldErrors.troupeau && (
            <span className="field-error-message">{fieldErrors.troupeau}</span>
          )}
          </div>
          <div className="form">
            <label>
              Géniteur {formData.date_naissance && <span className="required-indicator">*</span>}
            </label>
          {/* Sélection du père */}
          <div className={fieldErrors.pere ? "select-error-wrapper" : ""}>
            <Select name="pere" 
            placeholder="Sélectionner ou saisir le matricule"
            aria-required={!!formData.date_naissance}
    value={
      formData.pere
        ? { value: formData.pere, label: animaux.find((a) => a.id === formData.pere)?.matricule || formData.pere }
        : null
    }
    onChange={(selectedOption) => {
      setFormData({
        ...formData,
        pere: selectedOption ? selectedOption.value : ""
      });
      // Valider après changement
      if (touchedFields.pere || fieldErrors.pere) {
        const error = validateField("pere", selectedOption ? selectedOption.value : "", formData.date_naissance);
        setFieldErrors(prev => ({
          ...prev,
          pere: error
        }));
      }
    }}
    onBlur={() => {
      setTouchedFields(prev => ({ ...prev, pere: true }));
      const error = validateField("pere", formData.pere, formData.date_naissance);
      setFieldErrors(prev => ({ ...prev, pere: error }));
    }}
    className={fieldErrors.pere ? "react-select-error" : ""}
    options={animaux
      .filter((a) => a.sexe === "M")
      .map((a) => ({
        value: a.id,
        label: a.matricule,
      }))} />
      </div>
      {fieldErrors.pere && (
        <span className="field-error-message">{fieldErrors.pere}</span>
      )}
            
          </div>
          <div className="form">
            <label>
              Génitrice {formData.date_naissance && <span className="required-indicator">*</span>}
            </label>
          {/* Sélection de la mère */}
          <div className={fieldErrors.mere ? "select-error-wrapper" : ""}>
            <Select name="mere" 
            placeholder="Sélectionner ou saisir le matricule"
            aria-required={!!formData.date_naissance}
    value={
      formData.mere
        ? {
            value: formData.mere,
            label:
              animaux.find((a) => a.id === formData.mere)?.matricule ||
              formData.mere, // Si c'est une valeur saisie manuellement
          }
        : null
    }
    onChange={(selectedOption) => {
      setFormData({
        ...formData,
        mere: selectedOption ? selectedOption.value : "", // stocke ID ou texte saisi
      });
      // Valider après changement
      if (touchedFields.mere || fieldErrors.mere) {
        const error = validateField("mere", selectedOption ? selectedOption.value : "", formData.date_naissance);
        setFieldErrors(prev => ({
          ...prev,
          mere: error
        }));
      }
    }}
    onBlur={() => {
      setTouchedFields(prev => ({ ...prev, mere: true }));
      const error = validateField("mere", formData.mere, formData.date_naissance);
      setFieldErrors(prev => ({ ...prev, mere: error }));
    }}
    className={fieldErrors.mere ? "react-select-error" : ""}
    options={animaux
      .filter((a) => a.sexe === "F")
      .map((a) => ({
        value: a.id,
        label: `${a.matricule}`,
      }))} />
      </div>
      {fieldErrors.mere && (
        <span className="field-error-message">{fieldErrors.mere}</span>
      )}
            
          </div>

          <div className="form">
            <label>Ranch de provenance</label>
            <select
              name="ranch_provenance"
              value={formData.ranch_provenance || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "new") {
                  setShowNewRanchInput(true);
                  setFormData({ ...formData, ranch_provenance: "", ranch_provenance_nom_input: "" });
                } else {
                  setShowNewRanchInput(false);
                  setNewRanchName("");
                  setFormData({ ...formData, ranch_provenance: value ? Number(value) : "", ranch_provenance_nom_input: "" });
                }
              }}
            >
              <option value="">{t.selectRanch}</option>
              {ranches.map((ranch) => (
                <option key={ranch.id} value={ranch.id}>
                  {ranch.nom} {ranch.code && `(${ranch.code})`}
                </option>
              ))}
              <option value="new">{t.createNewRanch}</option>
            </select>
            
            {showNewRanchInput && (
              <input
                type="text"
                name="ranch_provenance_nom_input"
                placeholder={t.newRanchName}
                value={newRanchName}
                onChange={(e) => {
                  setNewRanchName(e.target.value);
                  setFormData({ ...formData, ranch_provenance_nom_input: e.target.value, ranch_provenance: "" });
                }}
                style={{ marginTop: "8px" }}
              />
            )}
          </div>
          </div>
          {/* Boutons d'action */}
        <div className="actions">
          <button 
            type="submit" 
            className="save-but"
            disabled={!hasChanges()}
          >
            <FaCheck />
            <span>{t.save}</span>
          </button>
          <button
            type="button"
            onClick={handleDeleteClick}
            className="delete-but"
          >
            <FaTrash />
            <span>{t.deleteAnimal}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/animaux")}
            className="cancel-but"
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        itemName={formData.matricule}
        itemType="matricule"
        consequences={[
          t.deleteWarning1,
          t.deleteWarning2,
          t.deleteWarning3,
          t.deleteWarning4,
          t.deleteWarning5,
        ]}
        requireConfirmation={true}
        isLoading={isDeleting}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
    </div>
  );
  
}
